<template>
  <SDialog :open="open" @update:open="emit('update:open', $event)">
    <SDialogContent
      :show-close-button="true"
      class="close-confirm-modal-v2 w-125! max-w-125!"
      data-cy="v2-confirm-close-modal"
      @open-auto-focus.prevent
    >
      <SDialogHeader>
        <SDialogTitle class="flex items-center gap-2">
          <LandmarkIcon class="w-5 h-5 text-[#044c55]" />
          {{ t("cashfloat.select-cash-destination") }}
        </SDialogTitle>
      </SDialogHeader>

      <div class="flex flex-col gap-4 py-2">
        <!-- Location dropdown — hidden when "stays in drawer" is checked -->
        <div :class="staysInDrawer ? 'opacity-40 pointer-events-none' : ''">
          <label class="text-sm font-medium block mb-1">
            {{ t("cashfloat.cash-intended-to") }}
          </label>
          <SSelect
            v-model="locationId"
            :disabled="staysInDrawer"
            data-cy="v2-confirm-close-location"
          >
            <SSelectTrigger class="w-full">
              <component
                v-if="selectedLocationOption"
                :is="selectedLocationOption.icon"
                class="w-4 h-4 mr-2"
                :class="selectedLocationOption.colorClass"
              />
              <SSelectValue :placeholder="t('cashfloat.choose-location')" />
            </SSelectTrigger>
            <SSelectContent>
              <SSelectItem
                v-for="opt in richLocationOptions"
                :key="opt.value"
                :value="opt.value"
              >
                <span class="flex items-center gap-2">
                  <span
                    class="inline-flex items-center justify-center w-6 h-6 rounded-md"
                    :class="opt.bgClass"
                  >
                    <component
                      :is="opt.icon"
                      class="w-3.5 h-3.5"
                      :class="opt.colorClass"
                    />
                  </span>
                  <span>{{ opt.label }}</span>
                </span>
              </SSelectItem>
            </SSelectContent>
          </SSelect>
        </div>

        <!-- "Money stays in the drawer" checkbox -->
        <label
          class="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-md px-4 py-4 cursor-pointer select-none"
          data-cy="v2-confirm-close-stays-in-drawer"
        >
          <input
            v-model="staysInDrawer"
            type="checkbox"
            class="w-4 h-4 rounded border-slate-300 accent-[#044c55] cursor-pointer"
            @change="onStaysInDrawerChange"
          />
          <span class="text-sm font-medium">
            {{ t("cashfloat.money-stays-in-the-drawer") }}
          </span>
        </label>

        <!-- Unsynced orders warning -->
        <div
          v-if="hasUnsyncedOrders"
          class="flex items-start gap-2 bg-amber-50/70 border border-amber-300 text-amber-900 rounded-md px-3 py-2.5 text-sm"
          data-cy="v2-confirm-close-sync-warning"
        >
          <InfoIcon class="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
          <p>
            <span class="font-semibold">
              {{ t("cashfloat.v2.close.orders-not-synced") }}
            </span>
            {{ t("cashfloat.v2.close.orders-not-synced-help") }}
          </p>
        </div>
      </div>

      <SDialogFooter>
        <div class="w-full flex items-center justify-between gap-3">
          <SButton
            type="button"
            variant="outline"
            data-cy="v2-confirm-close-add-transfer"
            @click="emit('add-transfer')"
          >
            <ArrowLeftRightIcon class="w-4 h-4 mr-2" />
            {{ t("cashfloat.v2.close.add-new-transfer") }}
          </SButton>
          <div class="flex items-center gap-3">
            <SButton
              type="button"
              variant="outline"
              data-cy="v2-confirm-close-cancel"
              @click="emit('update:open', false)"
            >
              {{ t("button.cancel") }}
            </SButton>
            <SButton
              type="button"
              :loading="isSubmitting"
              :disabled="!canConfirm"
              data-cy="v2-confirm-close-yes"
              @click="emit('confirm', { locationId, staysInDrawer })"
            >
              {{ t("button.confirm") }}
            </SButton>
          </div>
        </div>
      </SDialogFooter>
    </SDialogContent>
  </SDialog>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
  SSelect,
  SSelectContent,
  SSelectItem,
  SSelectTrigger,
  SSelectValue,
} from "@smeetz/ui-core";
import { ArrowLeftRightIcon, InfoIcon, LandmarkIcon } from "@smeetz/ui-icons";
import { CashLocationDeviceType } from "@/constants/cash-location";
import { locationVisualFor } from "@/utils/vue-helpers/cash-location";

type RawLocation = {
  value: number;
  label: string;
  deviceType?: CashLocationDeviceType | null;
};

type LocationOption = RawLocation & {
  icon: any;
  bgClass: string;
  colorClass: string;
};

const props = defineProps<{
  open: boolean;
  locationOptions: RawLocation[];
  isSubmitting?: boolean;
  hasUnsyncedOrders?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:open", value: boolean): void;
  (
    e: "confirm",
    payload: { locationId: number | null; staysInDrawer: boolean }
  ): void;
  (e: "add-transfer"): void;
}>();

const { t } = useI18n({});

const locationId = ref<number | null>(null);
const staysInDrawer = ref(false);

const richLocationOptions = computed<LocationOption[]>(() =>
  props.locationOptions.map((l) => ({
    ...l,
    ...locationVisualFor(l.deviceType),
  }))
);

const selectedLocationOption = computed(() =>
  richLocationOptions.value.find((o) => o.value === locationId.value)
);

const canConfirm = computed(
  () => staysInDrawer.value || locationId.value !== null
);

function onStaysInDrawerChange() {
  if (staysInDrawer.value) locationId.value = null;
}

watch(
  () => props.open,
  (open) => {
    if (open) {
      locationId.value = null;
      staysInDrawer.value = false;
    }
  }
);
</script>

<style lang="scss">
.close-confirm-modal-v2[data-slot="dialog-content"] [data-slot="dialog-close"] {
  top: 1.5rem;
  right: 1.5rem;
  opacity: 1;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
}
</style>
